/**
 * Add-to-cart popup: press feedback, thumbnail flight, badge bump + "+1 added"
 * tag, and the confirmation modal / mobile bottom sheet. Implements
 * design-reference/ADD-TO-CART-SPEC.md.
 *
 * Still registers as <cart-notification> — assets/product-form.js finds this
 * element once via `document.querySelector('cart-notification')` in its own
 * constructor (product-form.js:11) and calls three methods on it:
 * getSectionsToRender() (to build the `sections=` query param, product-form.js:38),
 * setActiveElement() (product-form.js:41, before the fetch) and
 * renderContents(parsedState) (product-form.js:96/104, after a successful
 * /cart/add.js). Those three methods are implemented below with the same
 * names/signatures Dawn's stock cart-notification.js used, so that contract
 * is unchanged — only what happens inside them is new.
 *
 * Money: every price/subtotal string shown here is read verbatim out of
 * data-attributes rendered by sections/deminas-cart-popup.liquid using
 * Liquid's `money`/`money_with_currency` filters. This file does no currency
 * math and never hardcodes a currency symbol.
 *
 * "Optimistic update" (spec, Behavior rules): steps 1-2 (button press,
 * thumbnail flight) are genuinely optimistic — they run immediately on
 * click and don't know or care whether the add will succeed. Steps 3-4
 * (badge bump, popup) are gated on the real /cart/add.js response instead
 * of being shown early and rolled back, because this theme has no
 * client-side money formatter (see hard constraint: never hardcode £) and
 * fabricating a subtotal figure client-side risks showing an incorrect one.
 * In practice the fetch resolves well inside 400ms on a normal connection,
 * so the visible timing matches the spec's table exactly; on a slow
 * connection the data-bearing steps simply wait rather than guess. See
 * revision-13-report.md for the full rationale.
 */
(function () {
  'use strict';

  var TIMING = {
    pressDuration: 120,
    flightStart: 120,
    flightDuration: 280,
    badgeStart: 400,
    popupStart: 500,
    popupOffsetAfterBadge: 100,
    popupExitDuration: 160,
    tagIn: 120,
    tagHold: 1500,
    tagOut: 150,
    autoDismiss: 6000,
    mobileBreakpoint: 768,
  };

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobileViewport() {
    return window.innerWidth < TIMING.mobileBreakpoint;
  }

  function isVisible(el) {
    return !!el && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  // The header basket (.deminas-header__cart) and the homepage hero basket
  // (.hero-industrial__cart) are mutually exclusive per template — see
  // sections/deminas-header.liquid's `{% unless request.page_type == 'index' %}`
  // guard and sections/hero-industrial.liquid's own nav. Resolving at
  // runtime (rather than assuming one or the other) is what lets the same
  // flight/badge code work on every template without a template check.
  function resolveBasketWrapper() {
    var header = document.querySelector('.deminas-header__cart');
    if (isVisible(header)) return header;
    var hero = document.querySelector('.hero-industrial__cart');
    if (isVisible(hero)) return hero;
    return null;
  }

  // The Add button's nearest product image: card-product.liquid's
  // .deminas-card__image on collection/search/homepage-grid cards, or
  // main-product.liquid's .deminas-gallery__main-image on the PDP (the
  // gallery is a grid sibling of the buy box the button lives in, not an
  // ancestor, hence climbing to .deminas-pdp__grid first).
  function resolveSourceImage(button) {
    var card = button.closest('.deminas-card');
    if (card) {
      var cardImg = card.querySelector('.deminas-card__image img');
      if (cardImg) return cardImg;
    }
    var pdpGrid = button.closest('.deminas-pdp__grid');
    if (pdpGrid) {
      var pdpImg = pdpGrid.querySelector('.deminas-gallery__main-image');
      if (pdpImg) return pdpImg;
    }
    return null;
  }

  function restartAnimationClass(el, className) {
    el.classList.remove(className);
    // eslint-disable-next-line no-unused-expressions
    void el.offsetWidth; // force reflow so re-adding the class restarts the CSS animation
    el.classList.add(className);
  }

  function parseSectionHTML(html) {
    try {
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      return null;
    }
  }

  class CartNotificationElement extends HTMLElement {
    constructor() {
      super();

      this.backdrop = document.getElementById('DeminasCartBackdrop');
      this.popup = document.getElementById('DeminasCartPopup');
      this.card = document.getElementById('DeminasCartPopupCard');
      this.itemImage = document.getElementById('DeminasCartPopupItemImage');
      this.itemTitle = document.getElementById('DeminasCartPopupItemTitle');
      this.itemMeta = document.getElementById('DeminasCartPopupItemMeta');
      this.itemPrice = document.getElementById('DeminasCartPopupItemPrice');
      this.subtotalLabel = document.getElementById('DeminasCartPopupSubtotalLabel');
      this.subtotalAmount = document.getElementById('DeminasCartPopupSubtotalAmount');
      this.continueButton = document.getElementById('DeminasCartPopupContinue');
      this.liveRegion = document.getElementById('DeminasCartLiveRegion');

      this.activeElement = null; // set via setActiveElement(), Dawn's own contract
      this.pendingButton = null; // the Add button our own click listener captured
      this.pendingClickTime = 0;
      this.dismissTimer = null;
      this.tagHideTimer = null;
      this.tagRemoveTimer = null;
      this.renderSeq = 0; // guards against an older add's slower response clobbering a newer one

      document.addEventListener('click', this.handleDelegatedClick.bind(this), true);
      document.addEventListener('keydown', this.handleKeydown.bind(this));

      if (this.backdrop) this.backdrop.addEventListener('click', this.close.bind(this));
      if (this.continueButton) this.continueButton.addEventListener('click', this.close.bind(this));
      if (this.card) {
        this.card.addEventListener('mouseenter', this.pauseAutoDismiss.bind(this));
        this.card.addEventListener('focusin', this.pauseAutoDismiss.bind(this));
        this.card.addEventListener('mouseleave', this.resetAutoDismiss.bind(this));
        this.card.addEventListener('focusout', this.resetAutoDismiss.bind(this));
      }

      if (typeof subscribe === 'function') {
        subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError.bind(this));
      }
    }

    // ---- Dawn's cart contract (assets/product-form.js) ----

    getSectionsToRender() {
      return [{ id: 'deminas-cart-popup' }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(parsedState) {
      try {
        this.cartItemKey = parsedState.key;
        var clickTime = this.pendingClickTime || performance.now();
        var elapsed = performance.now() - clickTime;
        var badgeDelay = prefersReducedMotion() ? 0 : Math.max(0, TIMING.badgeStart - elapsed);
        var self = this;

        this.renderSeq += 1;
        var mySeq = this.renderSeq;

        window.setTimeout(function () {
          // If a later add's response already applied while this one was
          // waiting out its badgeDelay, dropping this one avoids the popup
          // regressing back to an earlier product/subtotal (rapid repeat
          // adds can resolve out of order).
          if (mySeq !== self.renderSeq) return;
          self.presentConfirmed(parsedState);
        }, badgeDelay);
      } catch (e) {
        // Never let a rendering problem propagate into product-form.js's fetch
        // chain (constraint: subscribers/hooks in this path must not throw).
        console.error(e);
      }
    }

    // ---- Click handling: steps 1-2 are optimistic, fire immediately ----

    handleDelegatedClick(event) {
      var button = event.target && event.target.closest && event.target.closest('.deminas-buy-buttons__submit');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      this.pendingButton = button;
      this.pendingClickTime = performance.now();

      var reduced = prefersReducedMotion();

      if (!reduced) {
        restartAnimationClass(button, 'is-pressing');
      }

      if (!reduced && !isMobileViewport()) {
        var basketWrapper = resolveBasketWrapper();
        var sourceImage = basketWrapper && resolveSourceImage(button);
        if (basketWrapper && sourceImage) {
          window.setTimeout(function () {
            this.flyThumbnail(sourceImage, basketWrapper);
          }.bind(this), TIMING.flightStart);
        }
      }
    }

    flyThumbnail(sourceImage, basketWrapper) {
      var sourceRect = sourceImage.getBoundingClientRect();
      if (!sourceRect.width || !sourceRect.height) return;
      var targetRect = basketWrapper.getBoundingClientRect();
      if (!targetRect.width || !targetRect.height) return;

      var clone = document.createElement('img');
      clone.src = sourceImage.currentSrc || sourceImage.src;
      clone.alt = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.className = 'deminas-flight-clone';
      clone.style.left = sourceRect.left + 'px';
      clone.style.top = sourceRect.top + 'px';
      clone.style.width = sourceRect.width + 'px';
      clone.style.height = sourceRect.height + 'px';
      document.body.appendChild(clone);

      var startCenterX = sourceRect.left + sourceRect.width / 2;
      var startCenterY = sourceRect.top + sourceRect.height / 2;
      var endCenterX = targetRect.left + targetRect.width / 2;
      var endCenterY = targetRect.top + targetRect.height / 2;
      var deltaX = endCenterX - startCenterX;
      var deltaY = endCenterY - startCenterY;
      // Vertical motion outruns horizontal (0.7/0.92 vs 0.55/0.8 progress at the
      // same offsets) and an upward "lift" is subtracted mid-flight so the path
      // bows above the straight line — an arc, not a straight line, per spec.
      var arcLift = Math.min(140, Math.abs(deltaX) * 0.35 + 60);

      var removeClone = function () {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      };

      if (typeof clone.animate !== 'function') {
        removeClone();
        return;
      }

      var animation = clone.animate(
        [
          { transform: 'translate(0px, 0px) scale(1)', opacity: 1, offset: 0 },
          {
            transform: 'translate(' + deltaX * 0.55 + 'px, ' + (deltaY * 0.7 - arcLift) + 'px) scale(0.65)',
            opacity: 1,
            offset: 0.5,
          },
          {
            transform: 'translate(' + deltaX * 0.8 + 'px, ' + (deltaY * 0.92 - arcLift * 0.15) + 'px) scale(0.4)',
            opacity: 1,
            offset: 0.8,
          },
          { transform: 'translate(' + deltaX + 'px, ' + deltaY + 'px) scale(0.25)', opacity: 0, offset: 1 },
        ],
        { duration: TIMING.flightDuration, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' }
      );

      animation.onfinish = removeClone;
      animation.oncancel = removeClone;
    }

    // ---- Steps 3-4: gated on the real /cart/add.js + sections response ----

    presentConfirmed(parsedState) {
      var sectionHTML = parsedState.sections && parsedState.sections['deminas-cart-popup'];

      // The popup's contents are Liquid-rendered (money formatting lives
      // there, not here). Normally product-form.js asks for the section
      // alongside /cart/add.js, but that only works when the element it
      // resolved is our upgraded <cart-notification> AND the response
      // carried the section back. When it does not — which is what made
      // add-to-cart from product-card grids silently do nothing while the
      // product page worked — fetch the section directly instead of
      // bailing out with no popup and no error.
      if (!sectionHTML) {
        var retryState = parsedState;
        var self2 = this;
        fetch(window.location.pathname + '?sections=deminas-cart-popup', {
          headers: { Accept: 'application/json' }
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var html = data && data['deminas-cart-popup'];
            if (!html) return;
            retryState.sections = retryState.sections || {};
            retryState.sections['deminas-cart-popup'] = html;
            self2.presentConfirmed(retryState);
          })
          .catch(function (e) {
            console.error('[cart popup] could not load section', e);
          });
        return;
      }

      var doc = parseSectionHTML(sectionHTML);
      if (!doc) return;

      var summaryEl = doc.getElementById('DeminasCartPopupSummary');
      if (!summaryEl) return;

      var itemEl = parsedState.key ? doc.getElementById('DeminasCartPopupItem-' + parsedState.key) : null;

      this.bumpBadge(summaryEl.getAttribute('data-item-count'));

      var self = this;
      window.setTimeout(
        function () {
          self.openOrUpdatePopup(itemEl, summaryEl);
        },
        prefersReducedMotion() ? 0 : TIMING.popupOffsetAfterBadge
      );
    }

    bumpBadge(itemCount) {
      var wrapper = resolveBasketWrapper();
      if (!wrapper) return;
      var countEl = wrapper.querySelector('.deminas-header__cart-count, .hero-industrial__cart-count');
      if (countEl) {
        countEl.textContent = itemCount;
        if (!prefersReducedMotion()) {
          restartAnimationClass(countEl, 'is-bumping');
        }
      }
      // Liquid sets this label once at page load (`Basket, {{ cart.item_count }}
      // items`); keep it in sync so it doesn't go stale after an AJAX add.
      wrapper.setAttribute('aria-label', 'Basket, ' + itemCount + ' items');
      this.showAddedTag(wrapper);
    }

    showAddedTag(wrapper) {
      var existing = wrapper.querySelector('.deminas-cart-tag');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      if (this.tagHideTimer) window.clearTimeout(this.tagHideTimer);
      if (this.tagRemoveTimer) window.clearTimeout(this.tagRemoveTimer);

      var tag = document.createElement('span');
      tag.className = 'deminas-cart-tag';
      tag.setAttribute('aria-hidden', 'true');
      tag.textContent = '+1 added';
      wrapper.appendChild(tag);

      // eslint-disable-next-line no-unused-expressions
      void tag.offsetWidth; // force layout so the enter transition below actually runs
      tag.classList.add('is-visible');

      this.tagHideTimer = window.setTimeout(function () {
        tag.classList.remove('is-visible');
        tag.classList.add('is-hiding');
        this.tagRemoveTimer = window.setTimeout(function () {
          if (tag.parentNode) tag.parentNode.removeChild(tag);
        }, TIMING.tagOut);
      }.bind(this), TIMING.tagIn + TIMING.tagHold);
    }

    // ---- The popup itself ----

    openOrUpdatePopup(itemEl, summaryEl) {
      var title = itemEl ? itemEl.getAttribute('data-title') || '' : '';
      var image = itemEl ? itemEl.getAttribute('data-image') || '' : '';
      var imageAlt = itemEl ? itemEl.getAttribute('data-image-alt') || '' : '';
      var skuQty = itemEl ? itemEl.getAttribute('data-sku-qty') || '' : '';
      var price = itemEl ? itemEl.getAttribute('data-price') || '' : '';
      var itemCount = parseInt(summaryEl.getAttribute('data-item-count'), 10) || 0;
      var subtotal = summaryEl.getAttribute('data-subtotal') || '';

      if (this.itemImage) {
        if (image) {
          this.itemImage.src = image;
          this.itemImage.alt = imageAlt;
          this.itemImage.hidden = false;
        } else {
          this.itemImage.hidden = true;
        }
      }
      if (this.itemTitle) this.itemTitle.textContent = title;
      if (this.itemMeta) this.itemMeta.textContent = skuQty;
      if (this.itemPrice) this.itemPrice.textContent = price;
      if (this.subtotalLabel) {
        this.subtotalLabel.textContent = 'Basket subtotal (' + itemCount + (itemCount === 1 ? ' item)' : ' items)');
      }
      if (this.subtotalAmount) this.subtotalAmount.textContent = subtotal;

      this.announce(title + ' added to basket. Basket subtotal ' + subtotal + '.');

      if (this.isOpen()) {
        // Repeat add while open (spec, Behavior rules): never stack a second
        // popup — content above was already swapped and step 3 (badge bump)
        // already re-ran in bumpBadge(); just keep this one open and refresh
        // its dismiss timer instead of replaying the entrance animation.
        this.resetAutoDismiss();
        return;
      }

      this.open();
    }

    announce(text) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      var region = this.liveRegion;
      window.setTimeout(function () {
        region.textContent = text;
      }, 50);
    }

    isOpen() {
      return !!(this.popup && !this.popup.hidden);
    }

    open() {
      if (!this.popup || !this.backdrop) return;

      this.backdrop.hidden = false;
      this.popup.hidden = false;
      this.popup.classList.remove('is-closing');
      // eslint-disable-next-line no-unused-expressions
      void this.popup.offsetWidth; // force reflow before the enter transition classes below

      this.backdrop.classList.add('is-open');
      this.popup.classList.add('is-open');

      if (this.card) {
        trapFocus(this.card, this.card);
      }

      document.body.classList.add('deminas-cart-popup-open');
      this.resetAutoDismiss();
    }

    close() {
      if (!this.isOpen()) return;

      this.popup.classList.remove('is-open');
      this.popup.classList.add('is-closing');
      this.backdrop.classList.remove('is-open');
      this.clearAutoDismiss();
      document.body.classList.remove('deminas-cart-popup-open');

      var self = this;
      var exitDuration = prefersReducedMotion() ? 100 : TIMING.popupExitDuration;
      window.setTimeout(function () {
        self.popup.hidden = true;
        self.backdrop.hidden = true;
        self.popup.classList.remove('is-closing');
      }, exitDuration);

      removeTrapFocus(this.activeElement || this.pendingButton);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      if (!this.isOpen()) return;
      this.close();
    }

    resetAutoDismiss() {
      this.clearAutoDismiss();
      var self = this;
      this.dismissTimer = window.setTimeout(function () {
        self.close();
      }, TIMING.autoDismiss);
    }

    pauseAutoDismiss() {
      this.clearAutoDismiss();
    }

    clearAutoDismiss() {
      if (this.dismissTimer) {
        window.clearTimeout(this.dismissTimer);
        this.dismissTimer = null;
      }
    }

    // ---- Failure path ----
    // product-form.js publishes cartError synchronously, then immediately calls
    // its own handleErrorMessage(response.description) — which would overwrite
    // anything we write here in the same tick. Deferring with setTimeout(0)
    // lets that finish first so our copy (the spec's literal error string)
    // is what's left showing, without touching product-form.js itself. Steps
    // 3-4 above never fired for a failed add (they're gated on
    // renderContents, which product-form.js only calls on success), so there
    // is nothing to roll back.
    handleCartError() {
      try {
        var button = this.pendingButton;
        if (!button) return;
        var form = button.closest('product-form');
        if (!form) return;
        window.setTimeout(function () {
          var messageEl = form.querySelector('.product-form__error-message');
          var wrapper = form.querySelector('.product-form__error-message-wrapper');
          if (messageEl) messageEl.textContent = "Couldn't add that — try again";
          if (wrapper) wrapper.hidden = false;
        }, 0);
      } catch (e) {
        console.error(e);
      }
    }
  }

  if (!customElements.get('cart-notification')) {
    customElements.define('cart-notification', CartNotificationElement);
  }

  // ---- Repair product-form's cached popup reference -------------------------
  //
  // product-form.js resolves its popup ONCE, in its constructor:
  //
  //     this.cart = document.querySelector('cart-notification') || ...
  //
  // and product-form.js:72 hard-navigates to /cart whenever that is falsy.
  // On product-card grids the script is loaded from inside the first card,
  // mid-body, so a <product-form> can upgrade before <cart-notification> has
  // been defined — caching null permanently. The product page happened to
  // win that race, which is why add-to-cart opened the popup there and
  // silently jumped to /cart from collection and homepage grids.
  //
  // Rather than patch Dawn's product-form.js, hand the reference back to any
  // form that missed it. Idempotent, and a no-op when the race didn't occur.
  function healProductForms() {
    var popup = document.querySelector('cart-notification');
    if (!popup) return;
    document.querySelectorAll('product-form').forEach(function (form) {
      if (!form.cart) form.cart = popup;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', healProductForms);
  } else {
    healProductForms();
  }

  // Cards can also arrive later — facet filtering and pagination re-render the
  // grid through the Section Rendering API, replacing <product-form> elements
  // whose constructors run against the same race.
  if (typeof MutationObserver === 'function') {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          healProductForms();
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
